import { CellState } from './cell';
import { isMine } from './mineGenerator';

export type BoxStatus = 'ACTIVE' | 'COMPLETED' | 'BUSTED';

export interface BoxData {
  boxX: number;
  boxY: number;
  status: BoxStatus;
}

/**
 * Evaluates whether a 10x10 box (boxX, boxY) meets completion criteria:
 * Condition A: Every mine in the box is flagged AND zero incorrect flags exist.
 * Condition B: Every non-mine cell in the box has been revealed.
 */
export function isBoxCompleted(
  seed: string,
  boxX: number,
  boxY: number,
  getCellState: (x: number, y: number) => CellState,
  mineProbability: number = 0.15
): boolean {
  let totalMines = 0;
  let correctFlags = 0;
  let incorrectFlags = 0;
  let revealedSafeCells = 0;

  const startX = boxX * 10;
  const startY = boxY * 10;

  for (let cy = 0; cy < 10; cy++) {
    for (let cx = 0; cx < 10; cx++) {
      const x = startX + cx;
      const y = startY + cy;
      const mine = isMine(seed, x, y, mineProbability);
      const state = getCellState(x, y);

      if (mine) {
        totalMines++;
        if (state === 'FLAGGED') {
          correctFlags++;
        }
      } else {
        if (state === 'FLAGGED') {
          incorrectFlags++;
        } else if (state === 'REVEALED') {
          revealedSafeCells++;
        }
      }
    }
  }

  const totalSafeCells = 100 - totalMines;

  // Condition A: All mines flagged with 0 incorrect flags
  const conditionA = totalMines > 0 && correctFlags === totalMines && incorrectFlags === 0;

  // Condition B: All safe cells revealed
  const conditionB = revealedSafeCells === totalSafeCells;

  return conditionA || conditionB;
}
